/**
 * Utility to calculate Wiegand Parity and Controller representation
 */

function countSetBits(num) {
  let count = 0;
  let n = typeof num === 'bigint' ? num : BigInt(num);
  while (n > 0n) {
    count += Number(n & 1n);
    n >>= 1n;
  }
  return count;
}

/**
 * Calculates the credential value with parity bits according to Wiegand specifications.
 * 
 * 26-bit: 24 bits data (8-bit FC + 16-bit Card No). Bit 25 is Even parity over bits 23..12, Bit 0 is Odd parity over bits 11..0.
 * 32-bit: 32 bits data, no parity bits (returns null since parity is not applicable).
 * 48-bit: 45 bits data (22-bit FC + 23-bit Card No). Uses 3 parity bits (Bits 0, 46, and 47) using Corp 1000 48-bit style rules.
 */
export function calculateWiegandParityValue({ format, type, credentialValue, cardNumber, facilityCode }) {
  if (!format) return null;

  let rawValue = 0n;

  try {
    if (type === '2') {
      // Card Number & Facility Code
      if (cardNumber === undefined || cardNumber === '' || facilityCode === undefined || facilityCode === '') {
        return null;
      }
      const fc = BigInt(facilityCode);
      const cn = BigInt(cardNumber);

      if (format === '26 bits') {
        if (fc > 255n || cn > 65535n || fc < 0n || cn < 0n) return null;
        rawValue = (fc << 16n) | cn;
      } else if (format === '48 bits') {
        if (fc > 4194303n || cn > 8388607n || fc < 0n || cn < 0n) return null;
        rawValue = (fc << 23n) | cn;
      } else {
        return null;
      }
    } else {
      // Credential ID
      if (credentialValue === undefined || credentialValue === '') {
        return null;
      }
      rawValue = BigInt(credentialValue);
    }

    if (rawValue < 0n) return null;

    if (format === '26 bits') {
      if (rawValue > 16777215n) return null; // 24-bit max
      const data = rawValue & 0xFFFFFFn;
      const high12 = (data >> 12n) & 0xFFFn;
      const low12 = data & 0xFFFn;

      const evenParity = (countSetBits(high12) % 2) === 1 ? 1n : 0n;
      const oddParity = (countSetBits(low12) % 2) === 0 ? 1n : 0n;

      const valueWithParity = (evenParity << 25n) | (data << 1n) | oddParity;
      return valueWithParity.toString();
    }

    if (format === '32 bits') {
      // 32-bit Wiegand has 0 parity bits; no parity value to display
      return null;
    }

    if (format === '48 bits') {
      if (rawValue > 35184372088831n) return null; // 45-bit max
      
      let temp_credential_id = rawValue << 1n;

      // Generating even parity with 2nd Most significant bits (Bit 46)
      const p46_mask = 0x01B6DB6DB6DB6n;
      if (countSetBits(temp_credential_id & p46_mask) % 2 !== 0) {
        temp_credential_id = temp_credential_id | 0x400000000000n;
      }

      // Generating odd parity with least significant bits (Bit 0)
      const p0_mask = 0x036DB6DB6DB6Cn;
      if (countSetBits(temp_credential_id & p0_mask) % 2 === 0) {
        temp_credential_id = temp_credential_id | 1n;
      }

      // Generating odd parity with Most significant bits (Bit 47)
      const p47_mask = 0x7FFFFFFFFFFFn;
      if (countSetBits(temp_credential_id & p47_mask) % 2 === 0) {
        temp_credential_id = temp_credential_id | 0x800000000000n;
      }

      return temp_credential_id.toString();
    }
  } catch {
    return null;
  }

  return null;
}

